import { Flex, Text } from '@chakra-ui/react';
import {
  ButtonIcon,
  FileUpload,
  FormFeedback,
  OutlineCard
} from '@morressier/alembic';
import { useFormContext } from 'react-hook-form';
import { DropzoneOptions, ErrorCode } from 'react-dropzone';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import {
  getAllowedFileFormats,
  getFileExtension,
  getReferenceName
} from 'src/utils/file';
import {
  deleteUploadedFile,
  getSignedPrivateAccessUrl,
  getSignedPrivateUploadUrl
} from 'src/api/queries';
import axiosInstance from 'src/api/client';
import { AxiosProgressEvent } from 'axios';
import { FieldLabel } from 'src/components/Field';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { FormFieldCoverLetter } from '@publishing-engine/domain/types/article';
import { get } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { useDuplicateConfirm } from 'src/hooks/useDuplicateConfirm';

const CoverLetter = ({
  config,
  submissionId
}: {
  submissionId: string;
  config: FormFieldCoverLetter;
}) => {
  const {
    formState: { errors },
    register,
    watch,
    setError,
    setValue,
    resetField,
    getValues
  } = useFormContext<ISubmissionFormData>();

  const {
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;
  const { checkIsDuplicateFile, duplicateFileModal } = useDuplicateConfirm();
  const error = get(errors, field);
  const coverLetter = watch(field);

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: File[]) => {
    const accepted = acceptedFiles[0];
    resetField(field);
    const canUpload = await checkIsDuplicateFile(
      { fieldName: field, name: accepted?.name || '' },
      getValues().fileSet
    );
    if (accepted && canUpload) {
      coverLetter?.fileId && deleteUploadedFile(coverLetter.fileId);

      const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
        const { loaded, total = Number.POSITIVE_INFINITY } = progressEvent;
        const progress = Math.floor((loaded * 100) / total);

        fileToUpload = {
          ...fileToUpload,
          progress
        };

        setValue(field, fileToUpload);
      };

      const fileExtension = getFileExtension(accepted.name);
      let fileToUpload: ISubmissionFile = {
        name: accepted.name,
        referenceName: getReferenceName(accepted.name),
        progress: 0,
        isUploading: true,
        extension: fileExtension,
        url: ''
      };

      try {
        setValue(field, fileToUpload);

        const uploadParams = await getSignedPrivateUploadUrl(
          submissionId,
          fileExtension
        );
        if (!uploadParams) return;

        const formData = new FormData();
        formData.append('file', accepted, accepted.name);

        const {
          data: { id, file_url, original_filename }
        } = await axiosInstance.put<{
          id: string;
          file_url: string;
          original_filename: string;
        }>(uploadParams.signed_url, formData, {
          onUploadProgress
        });

        fileToUpload = {
          ...fileToUpload,
          fileId: id,
          url: file_url,
          name: original_filename,
          extension: fileExtension
        };

        setValue(field, fileToUpload);
      } catch (e) {
        setError(field, {
          type: 'upload-error',
          message: `Error uploading file ${(e as Error).message}`
        });
      } finally {
        setValue(field, {
          ...fileToUpload,
          isUploading: false
        });
      }
    }
  };

  const onDropRejected: DropzoneOptions['onDropRejected'] = rejectedFiles => {
    if (rejectedFiles[0]) {
      removeFile(coverLetter?.fileId);
      const rejectedFileError = rejectedFiles[0].errors[0];
      if (rejectedFileError?.code === 'file-invalid-type') {
        setError(field, {
          type: rejectedFileError.code,
          message:
            'Selected file format is not supported. Please upload PDF, DOC, or DOCX only.'
        });
      } else if (rejectedFileError?.code === 'file-too-large') {
        setError(field, {
          type: rejectedFileError.code,
          message: `File is larger than ${validations.maxSizeMB} MB`
        });
      } else if (rejectedFileError?.code === ErrorCode.TooManyFiles) {
        setError(field, {
          type: rejectedFileError.code,
          message: rejectedFileError.message
        });
      }
    }
  };

  const removeFile = async (fileId?: string) => {
    setValue(field, undefined);
    fileId && deleteUploadedFile(fileId);
  };

  const handleOpenFile = async (url: string) => {
    if (!submissionId) {
      return;
    }
    const signedUrl = await getSignedPrivateAccessUrl(submissionId, url);
    window.open(signedUrl, '_blank');
  };

  const allowedFormats = getAllowedFileFormats(validations.allowedFileFormats);

  return (
    <OutlineCard
      width="100%"
      padding={4}
      wordBreak={'break-word'}
      whiteSpace={'normal'}
    >
      <Flex flexDirection="column" gap={2}>
        <FieldLabel
          label={labels.main}
          subText={labels.subtitle}
          helpText={labels.helpText}
          isRequired={validations.required}
        />
        <Flex flexDirection="column" w={{ base: '100%', md: '50%' }}>
          <FileUpload
            {...register(field)}
            uploadButtonText="Select file"
            multiple={false}
            onDrop={onDrop}
            onDropRejected={onDropRejected}
            maxFiles={1}
            maxSize={validations.maxSizeMB * 1000000}
            isInvalid={Boolean(error)}
            accept={validations.allowedFileFormats}
            disabled={coverLetter?.isUploading}
            ariaLabel={'Upload Cover Letter'}
          />
        </Flex>
        <Text textStyle="textContext">
          Accepted format(s): {allowedFormats}. Max size:{' '}
          {validations.maxSizeMB}MB.
        </Text>

        {!!error && (
          <FormFeedback variant="error">
            {error.message === `must have required property 'CoverLetter'`
              ? 'Please upload a Cover Letter to continue'
              : error.message || 'No file selected'}
          </FormFeedback>
        )}
        <Flex alignItems="center" gap={1} width={'100%'}>
          {coverLetter?.isUploading ? (
            <span>
              {coverLetter.name}: Uploading {coverLetter.progress}%
            </span>
          ) : (
            <>
              {coverLetter?.url && (
                <>
                  <a
                    onClick={() => handleOpenFile(coverLetter?.url)}
                    style={{
                      cursor: 'pointer'
                    }}
                  >
                    <FormFeedback variant="success">
                      {coverLetter?.name}
                    </FormFeedback>
                  </a>
                  {!coverLetter?.isUploading && (
                    <ButtonIcon
                      size="small"
                      ariaLabel={`${coverLetter.name} delete`}
                      onClick={() => removeFile(coverLetter?.fileId)}
                      icon={<FontAwesomeIcon icon={faTrashAlt} />}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Flex>
      </Flex>
      {duplicateFileModal}
    </OutlineCard>
  );
};

export default CoverLetter;
